import { FC, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Lock, Eye, EyeOff, Loader2, ArrowRight, Cloud } from 'lucide-react';
import { setToken, authApi } from '@/api/auth';
import api from '@/api/client';
import { useAuth } from '@/hooks/useAuth';
import { SealMark } from '@/components/common/BrandLogo';

// 桌面端判定（preload 注入）
const isDesktop = !!window.psbDesktop?.isDesktop;

type Tab = 'login' | 'register' | 'reset';

// 密码复杂度口径与后端一致（大写+小写+数字，≥8 位）
const checkPasswordRules = (pw: string): string | null => {
  if (pw.length < 8) return '密码长度至少 8 位';
  if (!/[A-Z]/.test(pw)) return '密码必须包含至少一个大写字母';
  if (!/[a-z]/.test(pw)) return '密码必须包含至少一个小写字母';
  if (!/\d/.test(pw)) return '密码必须包含至少一个数字';
  return null;
};

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LoginModal: FC<LoginModalProps> = ({ isOpen, onClose }) => {
  const { login, register, sendCode, isLoggingIn, isRegistering, isSendingCode, loginError, registerError, sendCodeError } = useAuth();

  const [tab, setTab] = useState<Tab>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // 桌面端：云端账号直接登录（云端验证后自动开通本地会话）
  const [cloudServerUrl, setCloudServerUrl] = useState('https://grzhishiku.com');
  const [cloudLoading, setCloudLoading] = useState(false);
  // 密码找回（仅网页端；桌面端本机账号无邮箱链路不支持找回）
  const [resetSending, setResetSending] = useState(false);
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetDone, setResetDone] = useState(false);

  if (!isOpen) return null;

  const handleCloudLogin = async () => {
    setFormError(null);
    if (!email.trim()) { setFormError('请输入邮箱'); return; }
    if (!password) { setFormError('请输入密码'); return; }
    setCloudLoading(true);
    try {
      const { data } = await api.post('/api/v1/cloud-proxy/login-session', {
        server_url: cloudServerUrl.trim().replace(/\/+$/, ''),
        email: email.trim(),
        password,
      });
      setToken(data.access_token, rememberMe);
      window.location.reload();
    } catch (err: any) {
      setFormError(formatError(err));
    } finally {
      setCloudLoading(false);
    }
  };

  const resetForm = () => {
    setFormError(null);
    setPassword('');
    setVerificationCode('');
    setCountdown(0);
    setResetDone(false);
  };

  const switchTab = (t: Tab) => {
    setTab(t);
    resetForm();
  };

  const validateLogin = (): boolean => {
    if (!email.trim()) { setFormError('请输入邮箱'); return false; }
    if (!password) { setFormError('请输入密码'); return false; }
    return true;
  };

  const validateRegister = (): boolean => {
    if (!email.trim()) { setFormError('请输入邮箱'); return false; }
    if (!isDesktop && !verificationCode.trim()) { setFormError('请输入邮箱验证码'); return false; }
    if (!password) { setFormError('请输入密码'); return false; }
    const ruleError = checkPasswordRules(password);
    if (ruleError) { setFormError(ruleError); return false; }
    return true;
  };

  const formatError = (err: any): string => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail || err?.response?.data?.message;
    const msg = err?.message || '';

    if (status === 401) return '邮箱或密码错误，请检查输入';
    if (status === 400 && detail) return detail;
    if (status === 404) return '服务未找到，请检查后端是否启动';
    if (status >= 500) return '服务器错误，请稍后再试';
    if (msg.includes('Network Error') || msg.includes('ECONNREFUSED')) return '无法连接后端服务，请确认 localhost:8000 已启动';
    if (msg.includes('timeout')) return '请求超时，请检查网络连接';
    return detail || msg || '登录失败，请稍后重试';
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!validateLogin()) return;
    try {
      await login({ email, password, remember_me: rememberMe });
      onClose();
    } catch (err: any) {
      setFormError(formatError(err));
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!validateRegister()) return;
    try {
      await register({ email, password, ...(isDesktop ? {} : { verification_code: verificationCode }) });
      await login({ email, password, remember_me: rememberMe });
      onClose();
    } catch (err: any) {
      setFormError(formatError(err));
    }
  };

  const startCountdown = () => {
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendCode = async () => {
    setFormError(null);
    if (!email.trim()) {
      setFormError('请先输入邮箱');
      return;
    }
    try {
      await sendCode({ email });
      startCountdown();
    } catch (err: any) {
      setFormError(formatError(err));
    }
  };

  const handleSendResetCode = async () => {
    setFormError(null);
    if (!email.trim()) {
      setFormError('请先输入邮箱');
      return;
    }
    setResetSending(true);
    try {
      await authApi.forgotPassword({ email: email.trim() });
      startCountdown();
    } catch (err: any) {
      setFormError(formatError(err));
    } finally {
      setResetSending(false);
    }
  };

  const handleResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!email.trim()) { setFormError('请输入邮箱'); return; }
    if (!verificationCode.trim()) { setFormError('请输入邮箱验证码'); return; }
    if (!password) { setFormError('请输入新密码'); return; }
    const ruleError = checkPasswordRules(password);
    if (ruleError) { setFormError(ruleError); return; }
    setResetSubmitting(true);
    try {
      await authApi.resetPassword({
        email: email.trim(),
        code: verificationCode.trim(),
        new_password: password,
      });
      // 重置成功：回登录页，密码已清空，直接输新密码登录
      setTab('login');
      resetForm();
      setResetDone(true);
    } catch (err: any) {
      setFormError(formatError(err));
    } finally {
      setResetSubmitting(false);
    }
  };

  const isLoading = isLoggingIn || isRegistering;
  const errorMessage = formError || (loginError as any)?.message || (registerError as any)?.message || (sendCodeError as any)?.message || null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 20 }}
        transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
        className="relative z-10 w-full max-w-[400px] mx-4"
      >
        <div className="glass-card p-8">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 p-1.5 rounded-[2px] text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Brand */}
          <div className="relative z-10 flex flex-col items-center mb-8">
            <div className="mb-3">
              <SealMark size={44} />
            </div>
            <h1 className="text-xl font-bold text-text-primary tracking-tight">Molore</h1>
            <p className="text-sm text-text-secondary mt-1">登录以开始使用</p>
          </div>

          {/* Tabs（找回密码视图不显示切换条） */}
          {tab !== 'reset' && (
          <div className="relative z-10 flex gap-1 p-1 bg-bg-secondary rounded-[2px] border border-border-color mb-6">
            <button
              onClick={() => switchTab('login')}
              className={`flex-1 py-2 text-sm font-medium rounded-[2px] transition-all ${
                tab === 'login'
                  ? 'bg-bg-tertiary text-text-primary border border-border-color'
                  : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
              }`}
            >
              登录
            </button>
            <button
              onClick={() => switchTab('register')}
              className={`flex-1 py-2 text-sm font-medium rounded-[2px] transition-all ${
                tab === 'register'
                  ? 'bg-bg-tertiary text-text-primary border border-border-color'
                  : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
              }`}
            >
              注册
            </button>
          </div>
          )}

          {/* 密码重置成功提示 */}
          {resetDone && tab === 'login' && (
            <div className="relative z-10 mb-4 bg-success/10 border border-success/20 text-success rounded-[2px] px-4 py-3 text-sm">
              密码已重置，请使用新密码登录
            </div>
          )}

          {/* Error alert */}
          <AnimatePresence>
            {errorMessage && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="relative z-10 mb-4 bg-danger/10 border border-danger/20 text-danger rounded-[2px] px-4 py-3 text-sm"
              >
                {errorMessage}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Login Form */}
          <AnimatePresence mode="wait">
            {tab === 'login' && (
              <motion.form
                key="login"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                transition={{ duration: 0.2 }}
                onSubmit={handleLogin}
                className="relative z-10 space-y-4"
              >
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">邮箱</label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full pl-10 pr-4 py-2.5 bg-bg-secondary border border-border-color rounded-[2px] text-text-primary placeholder-text-muted focus:border-info/40 outline-none transition-all text-sm"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-xs font-medium text-text-secondary">密码</label>
                    {/* 桌面端本机账号无邮箱链路，不提供找回入口 */}
                    {!isDesktop && (
                      <button
                        type="button"
                        onClick={() => switchTab('reset')}
                        className="text-xs text-text-muted hover:text-accent transition-colors"
                        tabIndex={-1}
                      >
                        忘记密码？
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="输入密码"
                      className="w-full pl-10 pr-10 py-2.5 bg-bg-secondary border border-border-color rounded-[2px] text-text-primary placeholder-text-muted focus:border-info/40 outline-none transition-all text-sm"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <label className="flex items-center gap-2 text-xs text-text-secondary cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={isLoading || cloudLoading}
                    className="accent-[var(--accent)]"
                  />
                  7 天内保持登录
                </label>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-accent hover:bg-[var(--accent-hover)] text-white px-5 py-2.5 rounded-[2px] font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isLoggingIn ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      登录中...
                    </>
                  ) : (
                    <>
                      登录
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>

                {/* 桌面端：云端账号直接登录（本机账号与网页端账号是两套） */}
                {isDesktop && (
                  <div className="pt-3 mt-1 border-t border-border-color space-y-3">
                    <p className="text-[11px] text-text-muted leading-relaxed">
                      有网页端账号？用云端账号登录，首次会自动开通本地会话（数据仍存本机）
                    </p>
                    <input
                      type="text"
                      value={cloudServerUrl}
                      onChange={(e) => setCloudServerUrl(e.target.value)}
                      placeholder="云端服务器地址"
                      className="w-full px-3.5 py-2 bg-bg-secondary border border-border-color rounded-[2px] text-text-primary placeholder-text-muted focus:border-info/40 outline-none transition-all text-xs"
                      disabled={cloudLoading}
                    />
                    <button
                      type="button"
                      onClick={handleCloudLogin}
                      disabled={cloudLoading}
                      className="w-full bg-bg-tertiary hover:bg-bg-hover border border-accent/40 text-text-primary px-5 py-2.5 rounded-[2px] text-sm font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                      {cloudLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Cloud className="w-4 h-4 text-accent" />
                      )}
                      用云端账号登录
                    </button>
                  </div>
                )}
              </motion.form>
            )}

            {/* Register Form */}
            {tab === 'register' && (
              <motion.form
                key="register"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
                onSubmit={handleRegister}
                className="relative z-10 space-y-4"
              >
                {/* 桌面端：讲清本机账号的边界 */}
                {isDesktop && (
                  <div className="bg-info/10 border border-info/20 text-info rounded-[2px] px-4 py-3 text-xs leading-relaxed">
                    这里注册的是<strong>本机账号</strong>：只存在这台电脑上，<strong>不能</strong>用于
                    grzhishiku.com 网页端登录。想在多台设备间同步，请改用「登录」页的
                    <strong>用云端账号登录</strong>（数据同样存在本机）。
                  </div>
                )}
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">邮箱</label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full pl-10 pr-4 py-2.5 bg-bg-secondary border border-border-color rounded-[2px] text-text-primary placeholder-text-muted focus:border-info/40 outline-none transition-all text-sm"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* 桌面端本机注册免邮箱验证码（本地后端无 SMTP） */}
                {!isDesktop && (
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">邮箱验证码</label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                      <input
                        type="text"
                        inputMode="numeric"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="6 位验证码"
                        className="w-full pl-10 pr-4 py-2.5 bg-bg-secondary border border-border-color rounded-[2px] text-text-primary placeholder-text-muted focus:border-info/40 outline-none transition-all text-sm"
                        disabled={isLoading}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleSendCode}
                      disabled={isSendingCode || countdown > 0 || !email.trim()}
                      className="shrink-0 px-3 py-2.5 bg-bg-tertiary hover:bg-bg-hover border border-border-color rounded-[2px] text-xs text-text-secondary transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {isSendingCode ? '发送中...' : countdown > 0 ? `${countdown}s` : '获取验证码'}
                    </button>
                  </div>
                  <p className="text-[10px] text-text-muted mt-1.5">验证码将发送至您的邮箱，请查收</p>
                </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">密码</label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="至少 8 位，含大小写字母和数字"
                      className="w-full pl-10 pr-10 py-2.5 bg-bg-secondary border border-border-color rounded-[2px] text-text-primary placeholder-text-muted focus:border-info/40 outline-none transition-all text-sm"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-accent hover:bg-[var(--accent-hover)] text-white px-5 py-2.5 rounded-[2px] font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isRegistering ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      注册中...
                    </>
                  ) : (
                    <>
                      创建账户
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </motion.form>
            )}

            {/* Reset Password Form（网页端；桌面端不开放入口） */}
            {tab === 'reset' && (
              <motion.form
                key="reset"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
                onSubmit={handleResetSubmit}
                className="relative z-10 space-y-4"
              >
                <p className="text-xs text-text-secondary leading-relaxed">
                  输入注册邮箱，我们将发送验证码到您的邮箱，用于重置密码。
                </p>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">邮箱</label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full pl-10 pr-4 py-2.5 bg-bg-secondary border border-border-color rounded-[2px] text-text-primary placeholder-text-muted focus:border-info/40 outline-none transition-all text-sm"
                      disabled={resetSubmitting}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">邮箱验证码</label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                      <input
                        type="text"
                        inputMode="numeric"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="6 位验证码"
                        className="w-full pl-10 pr-4 py-2.5 bg-bg-secondary border border-border-color rounded-[2px] text-text-primary placeholder-text-muted focus:border-info/40 outline-none transition-all text-sm"
                        disabled={resetSubmitting}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleSendResetCode}
                      disabled={resetSending || countdown > 0 || !email.trim()}
                      className="shrink-0 px-3 py-2.5 bg-bg-tertiary hover:bg-bg-hover border border-border-color rounded-[2px] text-xs text-text-secondary transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {resetSending ? '发送中...' : countdown > 0 ? `${countdown}s` : '获取验证码'}
                    </button>
                  </div>
                  <p className="text-[10px] text-text-muted mt-1.5">如果该邮箱已注册，验证码将发送至您的邮箱</p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">新密码</label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="至少 8 位，含大小写字母和数字"
                      className="w-full pl-10 pr-10 py-2.5 bg-bg-secondary border border-border-color rounded-[2px] text-text-primary placeholder-text-muted focus:border-info/40 outline-none transition-all text-sm"
                      disabled={resetSubmitting}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={resetSubmitting}
                  className="w-full bg-accent hover:bg-[var(--accent-hover)] text-white px-5 py-2.5 rounded-[2px] font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {resetSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      重置中...
                    </>
                  ) : (
                    <>
                      重置密码
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => switchTab('login')}
                  className="w-full text-xs text-text-muted hover:text-text-primary transition-colors"
                >
                  返回登录
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginModal;
